/**
 * Shared helper functions for resolving drawing display data
 * with fallback logic from multiple sources (database fields, master data, etc.)
 */

import type { DrawingListItem } from '@/src/types/drawing-list-item'

/**
 * Strip file extension from a filename
 */
export const stripFileExtension = (name: string): string => {
  const lastDot = name.lastIndexOf('.')
  return lastDot > 0 ? name.slice(0, lastDot) : name
}

/**
 * Resolve drawing name from multiple possible sources
 * Priority: name -> fileName (without extension) -> master.data.name -> master.data.general[0] -> ''
 */
export const resolveDrawingName = (drawing: DrawingListItem | any): string => {
  // Check name (skip if empty string)
  if (drawing?.name && drawing.name.trim()) {
    return drawing.name;
  }
  
  // Check fileName
  if (drawing?.fileName) {
    const stripped = stripFileExtension(drawing.fileName);
    if (stripped && stripped.trim()) {
      return stripped;
    }
  }
  
  // Check master.data.name
  if (drawing?.master?.data?.name && drawing.master.data.name.trim()) {
    return drawing.master.data.name;
  }
  
  // Check master.data.general[0]
  if (drawing?.master?.data?.general?.[0] && drawing.master.data.general[0].trim()) {
    return drawing.master.data.general[0];
  }
  
  return '';
}

/**
 * Resolve drawing number from multiple possible sources
 * Priority: number -> master.data.number -> master.data.drawing_number -> master.data.drawingNumber -> ''
 */
export const resolveDrawingNumber = (drawing: DrawingListItem | any): string => {
  // Check number field (skip if empty string)  
  if (drawing?.number && drawing.number.trim()) {
    return drawing.number;
  }
  
  // Check master.data.number
  if (drawing?.master?.data?.number && drawing.master.data.number.trim()) {
    return drawing.master.data.number;
  }
  
  // Check master.data.drawing_number
  if (drawing?.master?.data?.drawing_number && drawing.master.data.drawing_number.trim()) {
    return drawing.master.data.drawing_number;
  }
  
  // Check master.data.drawingNumber
  if (drawing?.master?.data?.drawingNumber && drawing.master.data.drawingNumber.trim()) {
    return drawing.master.data.drawingNumber;
  }
  
  return '';
}

/**
 * Resolve drawing company from multiple possible sources
 * Priority: company -> master.data.company -> ''
 */
export const resolveDrawingCompany = (drawing: DrawingListItem | any): string => {
  return drawing?.company || drawing?.master?.data?.company || ''
}

// ============================================================================
// Drawing Base Data Extraction
// ============================================================================

/**
 * A single field definition for drawing base data display / entry.
 */
export type BaseDataField = {
  /** Property key within the schema object (e.g. 'numberOfTeeth') */
  key: string
  /** Human-readable label */
  label: string
  /** Mathematical symbol (e.g. 'z', 'mₙ') */
  symbol?: string
  /** Current value extracted from drawing data */
  value: number | string | null | undefined
  /** Unit string (e.g. 'mm', '°') */
  unit?: string
  /** Whether the field is optional (not always required) */
  optional?: boolean
  /** Input type used when rendering as a form field */
  inputType?: 'number' | 'text'
}

/**
 * Detects the primary schema type from drawing data.
 *
 * Returns 'gear' when gear data is present, 'tool' when only tool data is
 * present, and 'unknown' when neither is populated.
 */
export function detectSchemaType(
  drawingData: Record<string, any> | null | undefined,
): 'gear' | 'tool' | 'unknown' {
  if (!drawingData) return 'unknown'
  if (Array.isArray(drawingData.gear) && drawingData.gear.length > 0) return 'gear'
  if (Array.isArray(drawingData.tool) && drawingData.tool.length > 0) return 'tool'
  return 'unknown'
}

/**
 * Returns structured base data fields for a drawing based on the requested
 * schema type.  The `schemaName` parameter drives which set of fields is
 * returned, making the function extensible to future drawing types without
 * changing call sites.
 *
 * @param drawingData - Raw DrawingData (from master.data, overrides, etc.)
 * @param schemaName  - Schema type to extract: 'gear', 'tool', …
 * @param index       - Array index to read from (default 0, for multi-gear drawings)
 */
export function getDrawingBaseData(
  drawingData: Record<string, any> | null | undefined,
  schemaName: string,
  index = 0,
): BaseDataField[] {
  if (!drawingData) return []

  switch (schemaName) {
    case 'gear': {
      const gear = drawingData?.gear?.[index]

      // Tooth thickness definition: show the first available variant, otherwise
      // include an empty optional entry so the user can fill it in.
      const toothThicknessField: BaseDataField =
        gear?.toothThicknesNominal != null
          ? { key: 'toothThicknesNominal',             label: 'Tooth Thickness',        symbol: 'S_n',    value: gear.toothThicknesNominal,             unit: 'mm', optional: true, inputType: 'number' }
          : gear?.baseTangentLengthNominal != null
            ? { key: 'baseTangentLengthNominal',        label: 'Base Tangent Length',    symbol: 'W_k',    value: gear.baseTangentLengthNominal,          unit: 'mm', optional: true, inputType: 'number' }
            : gear?.measurementOverBallsPinsNominal != null
              ? { key: 'measurementOverBallsPinsNominal', label: 'Meas. Over Balls/Pins', symbol: 'M_{dm}', value: gear.measurementOverBallsPinsNominal,    unit: 'mm', optional: true, inputType: 'number' }
              : { key: 'toothThicknesNominal',             label: 'Tooth Thickness',       symbol: 'S_n',    value: undefined,                              unit: 'mm', optional: true, inputType: 'number' }

      return [
        { key: 'numberOfTeeth',       label: 'Number of Teeth',  symbol: 'z',    value: gear?.numberOfTeeth,                          unit: '-',  inputType: 'number' },
        { key: 'normalModule',        label: 'Normal Module',    symbol: 'm_n',  value: gear?.normalModule,                           unit: 'mm', inputType: 'number' },
        { key: 'normalPressureAngle', label: 'Pressure Angle',   symbol: 'α_n',  value: gear?.normalPressureAngle,                    unit: '°',  inputType: 'number' },
        { key: 'helixAngle',          label: 'Helix Angle',      symbol: 'β',    value: gear?.helixAngle ?? (gear ? 0 : undefined),   unit: '°',  inputType: 'number' },
        { key: 'faceWidth',           label: 'Face Width',       symbol: 'b',    value: gear?.faceWidth,                              unit: 'mm', inputType: 'number' },
        { key: 'tipDiameter',         label: 'Tip Diameter',     symbol: 'd_a',  value: gear?.tipDiameter,                            unit: 'mm', inputType: 'number' },
        { key: 'rootDiameter',        label: 'Root Diameter',    symbol: 'd_f',  value: gear?.rootDiameter,                           unit: 'mm', inputType: 'number' },
        toothThicknessField,
      ]
    }

    case 'tool': {
      const tool = drawingData?.tool?.[index]
      return [
        { key: 'name',                label: 'Tool Name',                             value: tool?.name,                                               inputType: 'text'   },
        { key: 'type',                label: 'Type',                                  value: tool?.type,                  optional: true,              inputType: 'text'   },
        { key: 'outsideDiameter',     label: 'Outside Diameter',  symbol: 'd_a',      value: tool?.outsideDiameter,       unit: 'mm', optional: true, inputType: 'number' },
        { key: 'numberOfTeeth',       label: 'Number of Teeth',   symbol: 'z',        value: tool?.numberOfTeeth,                    optional: true, inputType: 'number' },
        { key: 'normalPressureAngle', label: 'Pressure Angle',    symbol: 'α_n',      value: tool?.normalPressureAngle,   unit: '°',  optional: true, inputType: 'number' },
        { key: 'helixAngle',          label: 'Helix Angle',       symbol: 'β',        value: tool?.helixAngle,            unit: '°',  optional: true, inputType: 'number' },
      ]
    }

    default:
      return []
  }
}
